// src/components/Strzelnice.jsx
// Katalog strzelnic w Polsce - strona filarowa pod frazy lokalne
// („strzelnica <miasto>”, „strzelnice w województwie …”).
// Dane: src/data/strzelnice.js (plik generowany, patrz nagłówek pliku).
import React from 'react';
import { Link } from 'react-router-dom';
import styles from '../css/modules/Page.module.css';
import list from '../css/modules/Strzelnice.module.css';
import Faq from './Faq';
import JsonLd from '../seo/JsonLd';
import { OpinieProvider } from './Opinie';
import StrzelnicaKarta from './StrzelnicaKarta';
import { OBIEKTY, REGIONY, MIASTA, POWIATY, STRZELNICE_STATS } from '../data/strzelnice';
import { breadcrumbJsonLd, faqJsonLd, absoluteUrl } from '../seo/siteMeta';
import { trackEvent } from '../lib/analytics';
import { zLiczba, odmien, OBIEKTY_FORMS, MIEJSCOWOSCI_FORMS } from '../lib/odmiana';

const FAQ_ITEMS = [
  {
    question: 'Czy na strzelnicę można wejść bez pozwolenia na broń?',
    answer:
      'Tak. Na strzelnicach komercyjnych strzela się pod nadzorem prowadzącego strzelanie, z broni należącej do obiektu - pozwolenie nie jest potrzebne. Wystarczy dokument tożsamości, trzeźwość i ukończone 18 lat (osoby młodsze mogą strzelać za zgodą i w obecności opiekuna, jeżeli regulamin obiektu na to pozwala).',
  },
  {
    question: 'Ile kosztuje wejście na strzelnicę?',
    answer:
      'Ceny ustala każdy obiekt osobno. Zwykle płaci się za wynajem stanowiska (kilkadziesiąt złotych za godzinę) albo za pakiet z określoną liczbą strzałów, w którym mieści się broń, amunicja, tarcze i opieka instruktora. Osoby z własną bronią i amunicją płacą najczęściej tylko za stanowisko. Aktualny cennik zawsze sprawdzaj na stronie konkretnej strzelnicy.',
  },
  {
    question: 'Czym różni się strzelnica sportowa od komercyjnej?',
    answer:
      'Strzelnica sportowa działa najczęściej przy klubie zrzeszonym w PZSS: prowadzi treningi, zawody i przygotowuje do patentu strzeleckiego oraz licencji zawodniczej. Strzelnica komercyjna nastawiona jest na strzelania rekreacyjne, pakiety dla początkujących i imprezy. Wiele obiektów łączy obie role - klub trenuje na tej samej osi, na której w weekend strzelają goście.',
  },
  {
    question: 'Czy na strzelnicy zdaje się egzamin na patent strzelecki?',
    answer:
      'Część praktyczna egzaminu na patent strzelecki odbywa się na strzelnicy, przed komisją powołaną przez PZSS. Część teoretyczna to test wiedzy z przepisów, zasad bezpieczeństwa i regulaminów sportowych - tę przygotujesz w domu.',
  },
  {
    question: 'Jak przygotować się do pierwszej wizyty na strzelnicy?',
    answer:
      'Zarezerwuj termin telefonicznie lub przez stronę obiektu, zabierz dokument tożsamości, ubierz się w koszulkę pod szyję i buty pełne (łuska potrafi wpaść za dekolt), a długie włosy zwiąż. Ochronniki słuchu i okulary zwykle dostaniesz na miejscu. Przed strzelaniem podpiszesz oświadczenie o zapoznaniu się z regulaminem strzelnicy.',
  },
  {
    question: 'Skąd pochodzą dane w tym zestawieniu?',
    answer:
      'Podstawą jest baza OpenStreetMap (licencja ODbL), uzupełniona danymi kontaktowymi pobranymi ze stron internetowych samych strzelnic. Zestawienie nie jest urzędowym rejestrem - godziny, ceny i zasady wstępu potwierdzaj bezpośrednio w wybranym obiekcie.',
  },
];

const Strzelnice = () => {
  const { total, listed, phones, sites, regions } = STRZELNICE_STATS;

  return (
    <OpinieProvider>
      <JsonLd
        data={[
          breadcrumbJsonLd([
            { name: 'Strona główna', path: '/' },
            { name: 'Strzelnice w Polsce', path: '/strzelnice' },
          ]),
          faqJsonLd(FAQ_ITEMS),
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Strzelnice w Polsce według województw',
            numberOfItems: regions,
            itemListElement: REGIONY.map((r, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: `Strzelnice - województwo ${r.name}`,
              url: `${absoluteUrl('/strzelnice')}#${r.slug}`,
            })),
          },
        ]}
      />

      <main className={styles.page}>
        <article className={styles.wrap}>
          <nav className={styles.breadcrumbs} aria-label="Ścieżka nawigacji">
            <Link to="/">Strona główna</Link> › <span>Strzelnice w Polsce</span>
          </nav>

          <h1 className={styles.h1}>
            Strzelnice w Polsce - lista strzelnic według województw i miast
          </h1>

          <p className={styles.lead}>
            Zestawienie <strong>{total} obiektów strzeleckich</strong> w całej Polsce: strzelnice
            komercyjne, sportowe, klubowe i myśliwskie. Dla {listed} z nich mamy adres, telefon,
            stronę internetową lub godziny otwarcia. Lista jest ułożona{' '}
            <strong>według województw, a w każdym województwie według miast</strong> - wystarczy
            kliknąć swój region w spisie poniżej.
          </p>

          <p className={styles.updated}>
            Aktualizacja: lipiec 2026 · dane z OpenStreetMap (licencja ODbL) uzupełnione
            informacjami ze stron samych strzelnic
          </p>

          <ul className={list.stats}>
            <li>
              <span className={list.statValue}>{total}</span>
              <span className={list.statLabel}>{odmien(total, OBIEKTY_FORMS)} w bazie</span>
            </li>
            <li>
              <span className={list.statValue}>{listed}</span>
              <span className={list.statLabel}>z adresem lub kontaktem</span>
            </li>
            <li>
              <span className={list.statValue}>{phones}</span>
              <span className={list.statLabel}>z numerem telefonu</span>
            </li>
            <li>
              <span className={list.statValue}>{sites}</span>
              <span className={list.statLabel}>z własną stroną</span>
            </li>
          </ul>

          <nav className={list.toc} aria-label="Strzelnice według województw">
            <h2>Wybierz województwo</h2>
            <ul>
              {REGIONY.map(r => (
                <li key={r.slug}>
                  <Link to={`/strzelnice/wojewodztwo/${r.slug}`}>
                    strzelnice {r.name}
                    <span className={list.count}>{r.count}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav className={list.toc} aria-label="Strzelnice w największych miastach">
            <h2>Największe miasta i powiaty</h2>
            <ul>
              {MIASTA.map(c => (
                <li key={c.slug}>
                  <Link to={`/strzelnice/miasto/${c.slug}`}>
                    strzelnice {c.name}
                    <span className={list.count}>{c.count}</span>
                  </Link>
                </li>
              ))}
              {POWIATY.map(p => (
                <li key={p.slug}>
                  <Link to={`/strzelnice/powiat/${p.slug}`}>
                    {p.name}
                    <span className={list.count}>{p.count}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <p className={`${styles.note} ${list.accentNote}`}>
            Szukasz obiektu, który da się szybko sprawdzić i umówić? Zobacz{' '}
            <Link to="/najlepsze-strzelnice-w-polsce">
              ranking najlepiej udokumentowanych strzelnic w Polsce
            </Link>{' '}
            - z jawnymi kryteriami punktacji, bez zmyślonych ocen i gwiazdek.
          </p>

          <section className={styles.section} id="jak-korzystac">
            <h2>Jak korzystać z listy strzelnic</h2>
            <p>
              Przy każdym obiekcie podajemy tyle danych, ile udało się potwierdzić: nazwę, adres,
              numer telefonu, adres strony i - jeśli obiekt go publikuje - godziny otwarcia.
              Świadomie <strong>nie zgadujemy brakujących informacji</strong>: jeśli czegoś nie ma
              w wykazie, to znaczy, że nie potwierdziliśmy tej danej u źródła, a nie że obiekt jej
              nie ma.
            </p>
            <p>
              Zanim wybierzesz się na strzelnicę, <strong>zadzwoń i zarezerwuj termin</strong>.
              Część obiektów - zwłaszcza klubowych - działa tylko w wyznaczone dni, a strzelnice
              komercyjne w weekendy bywają zajęte na kilka dni do przodu. Godziny i ceny zmieniają
              się sezonowo, więc traktuj je jako punkt wyjścia, nie jako gwarancję.
            </p>
          </section>

          {REGIONY.map(region => (
            <section className={list.region} id={region.slug} key={region.slug}>
              <h2 className={list.regionTitle}>
                Strzelnice w województwie {region.loc}
                <span className={list.regionCount}>
                  {zLiczba(region.count, OBIEKTY_FORMS)}, {zLiczba(region.cities.length, MIEJSCOWOSCI_FORMS)}
                </span>
              </h2>

              {region.cities.map(city => (
                <div className={list.city} key={city.slug}>
                  <h3 className={list.cityTitle} id={`${region.slug}-${city.slug}`}>
                    Strzelnice {city.name}
                  </h3>
                  <ul className={list.cards}>
                    {city.idx.map(i => (
                      <StrzelnicaKarta item={OBIEKTY[i]} key={`${city.slug}-${OBIEKTY[i].n}`} />
                    ))}
                  </ul>
                </div>
              ))}

              {region.other.length > 0 && (
                <details className={list.other}>
                  <summary>
                    Pozostałe obiekty strzeleckie w województwie {region.loc} (
                    {region.other.length})
                  </summary>
                  <p className={list.otherNote}>
                    Te obiekty są ujęte w bazie map, ale nie publikują danych kontaktowych, które
                    moglibyśmy potwierdzić. Zwykle są to strzelnice klubowe, leśne i myśliwskie,
                    udostępniane po uzgodnieniu z zarządcą.
                  </p>
                  <ul className={list.otherList}>
                    {region.other.map(name => (
                      <li key={name}>{name}</li>
                    ))}
                  </ul>
                </details>
              )}
            </section>
          ))}

          <section className={styles.section} id="jak-wybrac">
            <h2>Jak wybrać strzelnicę dla siebie</h2>
            <h3>Strzelnica kryta czy otwarta</h3>
            <p>
              <strong>Strzelnica kryta</strong> działa niezależnie od pogody i pory roku, ma
              zwykle osie 15-25 m i nadaje się do treningu z bronią krótką. Na{' '}
              <strong>strzelnicy otwartej</strong> znajdziesz dłuższe dystanse - 50, 100, a czasem
              300 m i więcej - czyli warunki do strzelania z karabinu i do konkurencji
              dystansowych. Jeśli dopiero zaczynasz, kryta strzelnica w mieście będzie wygodniejsza.
            </p>
            <h3>Klub czy obiekt komercyjny</h3>
            <p>
              Jeżeli celem jest <Link to="/patent-strzelecki">patent strzelecki</Link> i docelowo
              pozwolenie na broń do celów sportowych, szukaj strzelnicy prowadzonej przez{' '}
              <strong>klub zrzeszony w PZSS</strong> - to klub potwierdza treningi, kieruje na
              egzamin i prowadzi licencję zawodniczą. Strzelnica czysto komercyjna świetnie
              sprawdzi się na pierwsze strzelanie i naukę podstaw, ale sama nie przeprowadzi Cię
              przez formalności.
            </p>
            <h3>Broń obiektu czy własna</h3>
            <p>
              Bez pozwolenia strzelasz z broni należącej do strzelnicy, pod nadzorem prowadzącego
              strzelanie. Posiadacze własnej broni płacą zazwyczaj tylko za wynajem stanowiska -
              warto sprawdzić, jakie kalibry i rodzaje amunicji dopuszcza regulamin danego obiektu,
              bo różnią się one nawet między sąsiednimi strzelnicami.
            </p>
          </section>

          <section className={styles.section} id="pierwsza-wizyta">
            <h2>Pierwsza wizyta na strzelnicy - co warto wiedzieć</h2>
            <ul>
              <li>
                <strong>Rezerwacja.</strong> Zadzwoń albo zarezerwuj termin online. Na miejscu
                podpiszesz oświadczenie o zapoznaniu się z regulaminem strzelnicy.
              </li>
              <li>
                <strong>Dokument i trzeźwość.</strong> Potrzebny jest dowód lub inny dokument ze
                zdjęciem. Osoby pod wpływem alkoholu nie zostaną dopuszczone do strzelania.
              </li>
              <li>
                <strong>Ubranie.</strong> Koszulka pod szyję i pełne buty - gorąca łuska potrafi
                wpaść za kołnierz. Długie włosy zwiąż, czapkę z daszkiem weź ze sobą.
              </li>
              <li>
                <strong>Ochronniki.</strong> Słuch i wzrok chroni się obowiązkowo; ochronniki
                i okulary zwykle są w cenie wejścia.
              </li>
              <li>
                <strong>Komendy prowadzącego.</strong> Na osi obowiązuje bezwzględne posłuszeństwo
                komendom „ładuj”, „start”, „stop”, „rozładuj”, „broń do przejrzenia”. Lufa zawsze
                skierowana w kulochwyt.
              </li>
            </ul>
            <p className={styles.note}>
              Zasady bezpieczeństwa i regulamin strzelnicy to jednocześnie materiał egzaminacyjny na
              patent strzelecki. Sprawdź je w zestawie{' '}
              <Link to="/test-bezpieczenstwo-strzelnica-egzamin-na-bron">
                pytań o bezpieczeństwo i regulamin strzelnicy
              </Link>
              .
            </p>
          </section>

          <div className={styles.ctaBox}>
            <h2>Strzelnica to praktyka. Teorię zdaj za pierwszym razem</h2>
            <p>
              Część praktyczna egzaminu odbywa się na strzelnicy, ale to teoria najczęściej
              przesądza o niezdanym egzaminie na patent strzelecki. Sprawdź się na prawdziwych
              pytaniach - bez rejestracji i bez opłat.
            </p>
            <div className={styles.ctaRow}>
              <Link
                to="/intro"
                className={styles.primaryButton}
                onClick={() => trackEvent('cta_click', { location: 'strzelnice', label: 'demo' })}
              >
                Rozwiąż darmowy test
              </Link>
              <Link
                to="/patent-strzelecki"
                className={styles.secondaryButton}
                onClick={() => trackEvent('cta_click', { location: 'strzelnice', label: 'patent' })}
              >
                Patent strzelecki - przewodnik
              </Link>
            </div>
          </div>

          <section className={styles.section} id="faq">
            <h2>Strzelnice - najczęstsze pytania</h2>
            <Faq items={FAQ_ITEMS} />
          </section>

          <section className={styles.section} id="zrodla">
            <h2>Skąd mamy te dane i jak zgłosić poprawkę</h2>
            <p>
              Podstawą zestawienia jest baza <strong>OpenStreetMap</strong> - © współtwórcy
              OpenStreetMap, dane udostępniane na licencji{' '}
              <a href="https://opendatacommons.org/licenses/odbl/" rel="noopener nofollow" target="_blank">
                ODbL
              </a>
              . Numery telefonów, adresy stron i godziny otwarcia uzupełniliśmy informacjami
              publikowanymi przez same strzelnice na ich witrynach. Z listy wyłączyliśmy obiekty
              wojskowe oraz oznaczone jako niedostępne publicznie.
            </p>
            <p>
              To nie jest urzędowy rejestr strzelnic - taki nie jest prowadzony centralnie,
              regulaminy strzelnic zatwierdzają organy gmin. Jeżeli prowadzisz strzelnicę, której
              tu brakuje, albo widzisz nieaktualne dane, <Link to="/kontakt">napisz do nas</Link> -
              poprawimy wpis przy najbliższej aktualizacji.
            </p>
          </section>
        </article>
      </main>
    </OpinieProvider>
  );
};

export default Strzelnice;
